package org.gymly.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.KeyboardArrowLeft
import androidx.compose.material.icons.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.KeyboardDoubleArrowLeft
import androidx.compose.material.icons.filled.KeyboardDoubleArrowRight
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.theme.GymlyColors
import org.gymly.util.formatIsoToDisplayDate
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.todayIsoDate

private val MONTH_NAMES = listOf(
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
)
private val WEEKDAY_LETTERS = listOf("L", "M", "M", "J", "V", "S", "D")

/** Años que se pueden elegir de la lista (cubre historial de membresías y reservas). */
private val YEAR_RANGE = 1990..2100

private fun isLeapYear(y: Int): Boolean = (y % 4 == 0 && y % 100 != 0) || y % 400 == 0

private fun daysInMonth(y: Int, m: Int): Int = when (m) {
    2 -> if (isLeapYear(y)) 29 else 28
    4, 6, 9, 11 -> 30
    else -> 31
}

/** Posición del día 1 del mes en la semana que empieza en lunes: 0 = lunes ... 6 = domingo. */
private fun firstWeekdayIndex(y: Int, m: Int): Int {
    val t = intArrayOf(0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4)
    val yy = if (m < 3) y - 1 else y
    val sundayBased = (yy + yy / 4 - yy / 100 + yy / 400 + t[m - 1] + 1) % 7
    return (sundayBased + 6) % 7
}

private fun isoOf(y: Int, m: Int, d: Int): String =
    "${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"

private fun splitIso(iso: String): Triple<Int, Int, Int>? {
    val p = iso.split("-")
    if (p.size != 3) return null
    val y = p[0].toIntOrNull() ?: return null
    val m = p[1].toIntOrNull() ?: return null
    val d = p[2].toIntOrNull() ?: return null
    return Triple(y, m, d)
}

/**
 * Calendario propio para elegir una fecha. Trabaja solo con texto `AAAA-MM-DD`, sin zonas horarias,
 * así que el día marcado es siempre el que se devuelve. Lo usan el panel admin y el portal.
 *
 * Para cambiar de año: las flechas dobles avanzan o retroceden un año, y tocar el mes/año abre la lista de años.
 *
 * [initialIso] es la fecha que aparece al abrir (si está vacía o no es válida, hoy).
 * [onConfirm] recibe la fecha elegida en `AAAA-MM-DD`.
 */
@Composable
fun IsoCalendarDialog(
    initialIso: String?,
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit,
    accent: Color = GymlyColors.Primary,
) {
    val start = parseFlexibleDateToIso(initialIso.orEmpty()) ?: todayIsoDate()
    val startParts = splitIso(start) ?: Triple(2000, 1, 1)
    var viewYear by remember { mutableStateOf(startParts.first) }
    var viewMonth by remember { mutableStateOf(startParts.second) }
    var selected by remember { mutableStateOf(start) }
    var showYears by remember { mutableStateOf(false) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Seleccionar fecha") },
        text = {
            Column {
                Text(
                    formatIsoToDisplayDate(selected),
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    color = accent,
                )
                Spacer(Modifier.height(8.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = { viewYear = (viewYear - 1).coerceIn(YEAR_RANGE) }) {
                        Icon(Icons.Filled.KeyboardDoubleArrowLeft, contentDescription = "Año anterior")
                    }
                    IconButton(onClick = {
                        if (viewMonth == 1) {
                            viewMonth = 12
                            viewYear -= 1
                        } else {
                            viewMonth -= 1
                        }
                    }) {
                        Icon(Icons.Filled.KeyboardArrowLeft, contentDescription = "Mes anterior")
                    }
                    Text(
                        "${MONTH_NAMES[viewMonth - 1].replaceFirstChar { it.uppercase() }} $viewYear",
                        modifier = Modifier
                            .weight(1f)
                            .clip(RoundedCornerShape(8.dp))
                            .clickable { showYears = !showYears }
                            .padding(vertical = 6.dp),
                        textAlign = TextAlign.Center,
                        fontWeight = FontWeight.SemiBold,
                        color = if (showYears) accent else GymlyColors.Text,
                    )
                    IconButton(onClick = {
                        if (viewMonth == 12) {
                            viewMonth = 1
                            viewYear += 1
                        } else {
                            viewMonth += 1
                        }
                    }) {
                        Icon(Icons.Filled.KeyboardArrowRight, contentDescription = "Mes siguiente")
                    }
                    IconButton(onClick = { viewYear = (viewYear + 1).coerceIn(YEAR_RANGE) }) {
                        Icon(Icons.Filled.KeyboardDoubleArrowRight, contentDescription = "Año siguiente")
                    }
                }

                if (showYears) {
                    val firstRow = ((viewYear - YEAR_RANGE.first) / 4 - 1).coerceAtLeast(0)
                    val gridState = rememberLazyGridState(initialFirstVisibleItemIndex = firstRow * 4)
                    LazyVerticalGrid(
                        columns = GridCells.Fixed(4),
                        state = gridState,
                        modifier = Modifier.fillMaxWidth().height(260.dp),
                    ) {
                        items(YEAR_RANGE.toList()) { year ->
                            val isCurrent = year == viewYear
                            Box(
                                Modifier
                                    .padding(3.dp)
                                    .clip(RoundedCornerShape(8.dp))
                                    .background(if (isCurrent) accent else Color.Transparent)
                                    .clickable {
                                        viewYear = year
                                        showYears = false
                                    }
                                    .padding(vertical = 10.dp),
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(
                                    year.toString(),
                                    color = if (isCurrent) Color.White else GymlyColors.Text,
                                    fontWeight = if (isCurrent) FontWeight.Bold else FontWeight.Normal,
                                )
                            }
                        }
                    }
                } else {
                    Row(Modifier.fillMaxWidth()) {
                        WEEKDAY_LETTERS.forEach { letter ->
                            Text(
                                letter,
                                modifier = Modifier.weight(1f),
                                textAlign = TextAlign.Center,
                                color = GymlyColors.TextMuted,
                                fontSize = 12.sp,
                            )
                        }
                    }
                    val lead = firstWeekdayIndex(viewYear, viewMonth)
                    val total = daysInMonth(viewYear, viewMonth)
                    val rows = (lead + total + 6) / 7
                    for (r in 0 until rows) {
                        Row(Modifier.fillMaxWidth()) {
                            for (c in 0 until 7) {
                                val day = r * 7 + c - lead + 1
                                if (day in 1..total) {
                                    val iso = isoOf(viewYear, viewMonth, day)
                                    val isSelected = iso == selected
                                    Box(
                                        Modifier
                                            .weight(1f)
                                            .height(40.dp)
                                            .padding(2.dp)
                                            .clip(CircleShape)
                                            .background(if (isSelected) accent else Color.Transparent)
                                            .clickable { selected = iso },
                                        contentAlignment = Alignment.Center,
                                    ) {
                                        Text(
                                            day.toString(),
                                            color = if (isSelected) Color.White else GymlyColors.Text,
                                            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal,
                                        )
                                    }
                                } else {
                                    Spacer(Modifier.weight(1f).height(40.dp))
                                }
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = { onConfirm(selected) }) {
                Text("Aceptar", color = accent)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancelar", color = GymlyColors.TextMuted)
            }
        },
    )
}
